import { useEffect, useRef, useState } from 'react'
import { LANGUAGES, type Language } from '@webbuilder/i18n'
import { useI18n } from '../lib/i18n'
import { type BillingPlan } from '../lib/api'
import { useBillingPlan } from '../lib/useBillingPlan'
import { roleLabelKey, type ProjectAccessRole } from '@webbuilder/shared-types'
import { IconChevronDown } from '@tabler/icons-react'
import { AccountMenu, ProfileAvatarContent, accentLabelKey, type VelticaAccent } from '@webbuilder/ui'
import { signOutEverywhere } from '../lib/editorSession'
import { supabase } from '../lib/supabase'

const PLAN_LABEL: Record<BillingPlan, string> = { free: 'Free', pro: 'Pro', business: 'Business' }

export function DashboardUserMenu({
  avatarUrl,
  userName,
  displayName,
  onOpenSettings,
  onOpenPlans,
  onOpenUsage,
  onOpenBilling,
  onOpenDomains,
  projectRole,
}: {
  avatarUrl: string
  userName: string
  displayName: string
  onOpenSettings: () => void
  /** La membresía de quien mira. Es de la persona, no del proyecto abierto. */
  onOpenPlans: () => void
  /** Lo gastado de la bolsa común: IA, almacenamiento, proyectos. También es de la persona. */
  onOpenUsage: () => void
  onOpenBilling: () => void
  /** Abre Facturación con la lista de dominios a la vista. */
  onOpenDomains: () => void
  /**
   * Tu rol en el proyecto abierto, cuando es de otra persona.
   *
   * Sin esto la pastilla enseñaba SIEMPRE el plan, que en un proyecto ajeno no
   * describe nada de lo que pasa ahí — y engaña en las dos direcciones: quien
   * está en el plan gratis dentro de un proyecto Business se cree limitado a
   * tres páginas, y quien está en Pro dentro de uno gratis choca con topes que
   * no espera. Los topes salen del plan del DUEÑO (ver `planForProject`).
   *
   * `owner` o ausente deja el plan, que ahí sí es lo que manda.
   */
  projectRole?: ProjectAccessRole
}) {
  const { t, language, setLanguage } = useI18n()
  const [open, setOpen] = useState(false)
  const { plan } = useBillingPlan()
  /* El correo de la cuenta, arriba del menú como en el editor: es lo que dice con qué cuenta se está. */
  const [email, setEmail] = useState('')
  useEffect(() => {
    let alive = true
    void supabase.auth.getSession().then(({ data }) => { if (alive) setEmail(data.session?.user.email ?? '') })
    return () => { alive = false }
  }, [])
  /* Sólo cuando de verdad eres invitado: `owner` y «no lo sé todavía» caen al
     plan, que es el comportamiento de siempre. */
  const guestRole = projectRole && projectRole !== 'owner' ? projectRole : null
  /* El acento ya no se lee aquí: la paleta vive dentro de `AccountMenu`, que lo
     toma de la preferencia compartida. Este componente sólo pone el disparador
     y el marco. */
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  /*
   * Al abrir, el foco va al MENÚ y no a su primera opción.
   *
   * Lo recibía «Ajustes de usuario», y a veces el navegador decidía que ese foco
   * merecía su anillo: el menú se abría con la primera fila pintada de azul,
   * como si estuviera elegida. Es una heurística —`:focus-visible` mira de dónde
   * venía el último gesto— y por eso pasaba unas veces sí y otras no.
   *
   * El contenedor lleva `tabIndex={-1}`: recibe el foco sin ser un mando, así
   * que no hay nada que pintar como seleccionado, y quien navega con teclado
   * sigue entrando en el menú — el siguiente tabulador cae en la primera opción.
   */
  useEffect(() => {
    if (!open) return
    const focusFrame = window.requestAnimationFrame(() => menuRef.current?.focus())
    return () => window.cancelAnimationFrame(focusFrame)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target)) return
      /* Los subpaneles del menú —idioma y color— se portan al `body`, así que
         para este oyente están «fuera». Sin esta comprobación, elegir un idioma
         cerraba el menú entero. */
      if ((target as Element).closest?.('.vl-account-sub')) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      /* Si hay un subpanel abierto, Escape es suyo: lo cierra él y deja el menú
         en pie. Sólo cuando no hay ninguno cierra esto. */
      if (document.querySelector('.vl-account-sub')) return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="dash-user-card dash-sidebar-user" ref={rootRef}>
      <button
        ref={triggerRef}
        className="dash-user-trigger"
        type="button"
        aria-label={t('userMenu.aria')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="dash-avatar">
          <ProfileAvatarContent name={userName} src={avatarUrl} />
        </span>
        <span className="dash-user-meta">
          <strong>{displayName}</strong>
          {/* Una sola regla: la pastilla enseña lo que GOBIERNA este proyecto
              para ti. En el tuyo, tu plan —es lo que pone los topes—; en uno
              compartido, tu rol —es lo que pone lo que puedes hacer—. En el
              mismo sitio y con la misma forma: moverlo dejaría el hueco vacío
              en un caso y los dos estados parecerían pantallas distintas. */}
          {guestRole ? (
            <span className="dash-plan-tier-badge dash-user-plan is-role">
              {t(roleLabelKey(guestRole))}
            </span>
          ) : (
            <span className={`dash-plan-tier-badge dash-user-plan is-${plan}`}>{PLAN_LABEL[plan]}</span>
          )}
        </span>
        <IconChevronDown className="dash-user-chevron" size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="project-menu dash-user-menu" role="menu" ref={menuRef} tabIndex={-1}>
          <AccountMenu
            labels={{
              settings: t('userMenu.settings'),
              language: t('language.label'),
              plans: t('userMenu.plans'),
              usage: t('userMenu.usage'),
              billing: t('userMenu.billing'),
              domains: t('userMenu.domains'),
              theme: t('userMenu.theme'),
              themeLight: t('userMenu.themeLight'),
              themeDark: t('userMenu.themeDark'),
              themeSystem: t('userMenu.themeSystem'),
              color: t('userMenu.color'),
              colorPanel: t('userMenu.colorPanel'),
              signOut: t('userMenu.signOut'),
            }}
            language={language}
            languages={LANGUAGES}
            languageLabel={(code: string) => t(`language.${code}`)}
            onLanguage={(code: string) => setLanguage(code as Language)}
            accentLabel={(accent: VelticaAccent) => t(accentLabelKey(accent))}
            onOpenSettings={onOpenSettings}
            onOpenPlans={onOpenPlans}
            onOpenUsage={onOpenUsage}
            onOpenBilling={onOpenBilling}
            onOpenDomains={onOpenDomains}
            onSignOut={() => { void signOutEverywhere().catch(() => {}) }}
            onClose={() => setOpen(false)}
            header={email ? (
              <>
                <div className="dash-user-menu-head">
                  <strong>{displayName}</strong>
                  <small>{email}</small>
                </div>
                <div className="vl-account-rule" />
              </>
            ) : null}
          />
        </div>
      ) : null}
    </div>
  )
}
